import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { identifyUser, clearUserIdentification } from '../utils/amplitude';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phoneNumber: string;
  marketingConsent: boolean;
}

interface LoginResponse {
  success: boolean;
  data: {
    token: string;
  };
}

interface ProfileResponse {
  success: boolean;
  data: User;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
  marketingConsent?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  fetchProfile: () => Promise<void>;
  handleAuthError: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    try {
      const response = await api.get<ProfileResponse>('/auth/profile');
      const userData = response.data.data;
      setUser(userData);
      
      // Identify user in Amplitude after profile is fetched
      identifyUser(userData.id, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        role: userData.role,
      });
    } catch (error: any) {
      console.error('Failed to fetch profile:', error);
      
      // Only clear user data on authentication errors (401, 403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Clear user data and redirect to login for security
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        // Redirect to login after clearing auth data
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('token');
    
    if (storedToken) {
      setToken(storedToken);
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        email,
        password
      });

      const { token } = response.data.data;
      
      // Store token in localStorage
      localStorage.setItem('token', token);
      
      // Update state and fetch profile (which will identify user)
      setToken(token);
      await fetchProfile();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await api.post<LoginResponse>('/auth/register', data);
      const { token } = response.data.data;
      
      // Store token in localStorage
      localStorage.setItem('token', token);
      
      // Update state and fetch profile (which will identify user)
      setToken(token);
      await fetchProfile();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear Amplitude user identification
      clearUserIdentification();
      
      // Clear storage and state
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  // Global function to handle 401 errors from any component
  const handleAuthError = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      register,
      logout,
      isAuthenticated: !!token,
      loading,
      fetchProfile,
      handleAuthError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 